import { forwardRef, useCallback, useEffect } from 'react'
import { CaptionProps, DateRange, DayPicker, Matcher, useNavigation } from 'react-day-picker'
import { Dialog, DialogPanel } from '@headlessui/react'
import { formatWeekdayName, Holiday, isHoliday } from './overrides'
import { Cancel, NavArrowLeft, NavArrowRight } from '../at-icon'
import { format, isSameDay, isWeekend } from 'date-fns'

interface CalendarDialogProps {
  id?: string
  styles: { [p: string]: React.CSSProperties }
  attributes: { [p: string]: { [p: string]: string } | undefined }
  open: boolean
  onClose: () => void
  mode: 'single' | 'range' | undefined
  selected: DateRange | Date | undefined
  /** Additional dates to disable, due to business requirements by default are blocked weekends and certain holiday days */
  disabled?: Matcher[]
  fromDate?: Date
  toDate?: Date
  required?: boolean
  onSelect: ((date: Date | DateRange | undefined) => void) | undefined
  holidays?: Holiday
}

const todayDate = new Date()

export const CalendarDialog = forwardRef<HTMLDivElement, CalendarDialogProps>(
  (
    {
      id,
      attributes,
      mode,
      onClose,
      onSelect,
      open,
      selected,
      fromDate,
      toDate,
      styles,
      required,
      disabled = [],
      holidays,
    },
    ref
  ) => {
    const CustomCaption = (props: CaptionProps) => {
      const { goToMonth, nextMonth, previousMonth } = useNavigation()
      return (
        <>
          <div className="mb-5 mt-2 w-full relative">
            <button title="Close calendar" onClick={onClose}>
              <Cancel className="text-primary-700 absolute top-2 right-2" />
            </button>
          </div>
          <div className="rdp-caption w-[92%] mx-auto">
            <div className="rdp-caption_label" aria-live="polite" role="presentation" id="react-day-picker-1">
              {format(props.displayMonth, 'MMMM yyy')}
            </div>
            <div className="rdp-nav">
              <button
                className="rdp-nav_icon rdp-button_reset rdp-button rdp-nav_button rdp-nav_button_previous"
                disabled={!previousMonth}
                onClick={() => previousMonth && goToMonth(previousMonth)}
              >
                <NavArrowLeft />
              </button>
              <button
                className="rdp-nav_icon rdp-button_reset rdp-button rdp-nav_button rdp-nav_button_next"
                disabled={!nextMonth}
                onClick={() => nextMonth && goToMonth(nextMonth)}
              >
                <NavArrowRight />
              </button>
            </div>
          </div>
        </>
      )
    }

    const findNextValidDate = useCallback(
      (date?: Date, holidays?: Holiday): Date => {
        let candidate = new Date(date || 'now')
        for (let i = 0; i <= 31; i++) {
          const weekend = isWeekend(candidate)
          const holidayBlocked = isHoliday(candidate, holidays)
          if (!weekend && !holidayBlocked) {
            return candidate
          }
          candidate = new Date(candidate)
          candidate.setDate(candidate.getDate() + 1)
        }
        return candidate
      },
      [holidays]
    )

    const normalizeInitialSelection = useCallback(
      (selected: Date | DateRange | undefined, holidays?: Holiday): Date | DateRange | undefined => {
        if (!selected) return selected

        if ('from' in (selected as any)) {
          const range = selected as DateRange
          let from = range.from
          let to = range.to
          if (from && (isWeekend(from) || isHoliday(from, holidays))) {
            from = findNextValidDate(from, holidays)
          }
          if (to && (isWeekend(to) || isHoliday(to, holidays))) {
            to = findNextValidDate(to, holidays)
          }
          return { from, to }
        }

        if (selected instanceof Date) {
          if (isWeekend(selected) || isHoliday(selected, holidays)) {
            return findNextValidDate(selected, holidays)
          }
        }

        return selected
      },
      [holidays, findNextValidDate]
    )

    useEffect(() => {
      if (id === 'or-filter-section-date-calendar-dialog') return
      if (mode === 'range') {
        const normalizedSelected = normalizeInitialSelection(
          { from: (selected as DateRange)?.from || fromDate },
          holidays
        )
        onSelect?.(normalizedSelected)
      }
      if (mode === 'single') {
        const normalizedSelected = normalizeInitialSelection(selected || todayDate, holidays)
        onSelect?.(normalizedSelected)
      }
    }, [mode, holidays])

    useEffect(() => {
      if (open) {
        document.body.style.overflow = 'auto'
      } else {
        document.body.style.overflow = 'unset'
      }
      return () => {
        document.body.style.overflow = 'unset'
      }
    }, [open])

    return (
      <Dialog
        ref={ref}
        style={styles.popper}
        {...attributes.popper}
        open={open}
        onClose={onClose}
        className="w-[fit-content] bg-neutrals-white z-[51]"
      >
        <DialogPanel id={id}>
          {/* @ts-expect-error Types are divided based on mode, using mixed approach here */}
          <DayPicker
            initialFocus={open}
            fromDate={fromDate}
            toDate={toDate}
            mode={mode}
            disabled={[isWeekend, (date: Date) => isHoliday(date, holidays), ...disabled]}
            components={{
              Caption: CustomCaption,
            }}
            selected={selected}
            defaultMonth={
              (selected as DateRange)?.to ||
              (selected as DateRange)?.from ||
              (selected instanceof Date && selected) ||
              findNextValidDate(todayDate, holidays)
            }
            onSelect={(date: Date | DateRange | undefined) => {
              if (!date) return onSelect?.(undefined)

              const corrected = normalizeInitialSelection(date, holidays)

              if (
                corrected &&
                'from' in corrected &&
                corrected.from &&
                corrected.to &&
                isSameDay(corrected.from, corrected.to)
              ) {
                onSelect?.(undefined)
              } else {
                onSelect?.(corrected)
              }
            }}
            required={required}
            formatters={{ formatWeekdayName }}
          />
        </DialogPanel>
      </Dialog>
    )
  }
)
